<template>
	<div
		v-show="isVisible"
		:ref="position"
		class="dark trim-handle rounded-full capitalize"
		:style="{width: `${trimHandleWidthInPx}px`}"
		:class="[
			position === 'start' && 'start',
			position === 'end' && 'end',
			domTrimmingStore.isTrimming && domTrimmingStore.activeTrimHandle === position ? 'is-trimming' : '',
		]"
		:draggable="true"
		@dragstart="onDragStart"
		@drag="onDrag"
		@dragend="onDragEnd"
		@click.stop="domTrimmingStore.activeTrimHandle = position"
		@mousedown="onMouseDown"
		@mouseup="onMouseUp"
	>
		{{ position }}
		<canvas ref="canvas" width="1" height="1" style="z-index: -1"></canvas>
	</div>
</template>

<script>
import {useSessionStore} from 'shared/stores/session'

export default {
	name: 'DomTrimHandle',
	props: {
		isVisible: Boolean,
		// these trim handles are specific to dom trimming and can have only two possible values. 'start' and 'end',
		// start handle would be on the left most of progress bar and is allowed to be dragged to the right end
		// of progress, without overlapping/surpassing 'end' handle
		position: {type: String, validator: position => ['start', 'end'].includes(position)},
	},
	setup() {
		return {sessionStore: useSessionStore()}
	},
	data() {
		return {
			trimHandleWidthInPx: 60,
		}
	},
	computed: {
		domTrimmingStore() {
			return this.sessionStore.domTrimming
		},
	},
	watch: {
		'domTrimmingStore.domProgressBarPositionInPx.width': {
			handler: 'computeTrimHandlesPosition',
		},
	},
	methods: {
		computeTrimHandlesPosition() {
			const {trimStartPosition, trimEndPosition, domProgressBarPositionInPx} = this.domTrimmingStore
			if (trimStartPosition && this.$refs.start) {
				this.$refs.start.style.left = `${
					trimStartPosition - domProgressBarPositionInPx.left - this.trimHandleWidthInPx
				}px`
			} else if (trimEndPosition && this.$refs.end) {
				this.$refs.end.style.right = `${
					domProgressBarPositionInPx.right - trimEndPosition - this.trimHandleWidthInPx
				}px`
			}
		},
		onDragStart(event) {
			// blankCanvas is added to hide the ghost drag image in chrome. Setting the image to empty image
			// works but then chrome displays it's gray globe icon upon drag
			// https://stackoverflow.com/questions/6771196/stopping-chrome-from-changing-cursor-to-a-globe-while-dragging-a-link
			event.dataTransfer.setDragImage(this.$refs.canvas, 0, 0)
			this.domTrimmingStore.isTrimming = true
			this.domTrimmingStore.activeTrimHandle = this.position
		},
		onDrag(event) {
			this.domTrimmingStore.isTrimming = true
			this.domTrimmingStore.activeTrimHandle = this.position

			const {domProgressBarPositionInPx} = this.domTrimmingStore
			if (
				!(
					event.clientX >= domProgressBarPositionInPx.left - 2 &&
					event.clientX <= domProgressBarPositionInPx.right + 2
				)
			)
				return

			const {trimStartPosition, trimEndPosition} = this.domTrimmingStore
			// trim handle with position 'start' should not go over the trim handle with position 'end' or
			// progress bar's right
			if (this.position === 'start' && event.clientX <= (trimEndPosition || domProgressBarPositionInPx.right)) {
				this.$refs.start.style.left = `${
					event.clientX - domProgressBarPositionInPx.left - this.trimHandleWidthInPx
				}px`
				this.domTrimmingStore.trimHandleXPositionInPx = event.clientX
			}

			// trim handle with position 'end' should not go over the trim handle with position 'start' or
			// progress bar's left
			else if (
				this.position === 'end' &&
				event.clientX >= (trimStartPosition || domProgressBarPositionInPx.left)
			) {
				this.$refs.end.style.right = `${
					domProgressBarPositionInPx.right - event.clientX - this.trimHandleWidthInPx
				}px`
				this.domTrimmingStore.trimHandleXPositionInPx = event.clientX
			}
		},
		onDragEnd() {
			document.getElementById('blank-canvas')?.remove()

			this.domTrimmingStore.isTrimming = false
			this.domTrimmingStore.activeTrimHandle = null
			this.domTrimmingStore.onlyPlayTrimmedPart = true
		},
		onMouseDown() {
			this.domTrimmingStore.activeTrimHandle = this.position
			this.domTrimmingStore.isTrimming = true
		},
		onMouseUp() {
			this.domTrimmingStore.isTrimming = false
			this.domTrimmingStore.activeTrimHandle = null
		},
	},
}
</script>

<style scoped>
.trim-handle {
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: grabbing;
	position: absolute;
	text-align: center;
	height: 26px;
	bottom: 24px;
	border: 1px solid var(--yellow-500);
	background-color: var(--yellow-500);
	box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.3);
	color: var(--gray-900);
	user-select: none;
}

.trim-handle:hover {
	background-color: var(--yellow-400);
	border-color: var(--yellow-400);
}

.trim-handle.start {
	left: -61px;
	border-bottom-right-radius: 0px !important;
}

.trim-handle.start::before {
	content: '';
	position: absolute;
	width: 0;
	height: 0;
	border-left: 12px solid transparent;
	border-right: 0px solid transparent;
	border-top: 10px solid var(--yellow-500);
	bottom: -5px;
	right: -1px;
}

.trim-handle.start:hover::before {
	border-top: 10px solid var(--yellow-400) !important;
}

.trim-handle.start.is-trimming {
	border: 1px solid var(--yellow-400);
	background-color: var(--yellow-400);
	color: var(--gray-650);
}

.trim-handle.start.is-trimming::before {
	border-top: 10px solid var(--yellow-400) !important;
}

.trim-handle.end {
	right: -61px;
	border-bottom-left-radius: 0px !important;
}

.trim-handle.end::before {
	content: '';
	position: absolute;
	width: 0;
	height: 0;
	border-left: 0px solid transparent;
	border-right: 12px solid transparent;
	border-top: 10px solid var(--yellow-500);
	bottom: -5px;
	left: -1px;
}

.trim-handle.end:hover::before {
	border-top: 10px solid var(--yellow-400) !important;
}

.trim-handle.end.is-trimming {
	border: 1px solid var(--yellow-400);
	background-color: var(--yellow-400);
	color: var(--gray-650);
}

.trim-handle.end.is-trimming::before {
	border-top: 10px solid var(--yellow-400) !important;
}

.trim-handle.is-trimming {
	border: 1px solid var(--yellow-400);
	background-color: var(--yellow-400);
	color: var(--gray-650);
}

.trim-handle.is-trimming::before {
	border-top: 10px solid var(--yellow-400);
}
</style>
